import { P2GAlert, P2GBox } from '@parcel2go/core-ui';
import { P2GButton } from '@parcel2go/core-ui';
import { P2GText } from '@parcel2go/core-ui';
import { useMemo, useState } from 'react';
import { Country, InputRuleApiModel, PackageApiModel, SmartSendItemApiModel } from '~/lib/api/smartsend/types';
import { DimensionsSelection } from '../DimensionsSelection';
import { SmartSendItemGroup } from '../item/SmartSendGroup';
import SidePanelSection, { SidePanelSections } from '../../../../../components/sidepanel-section';
import { FilterToSendableItems } from '../panels/FilterToSendableItems';
import { ParcelCountPicker } from '../panels/ParcelCountPicker';
import { parcelCountBound } from '~/apps/smartsend/lib/parcels/bounds';
import { ParcelArranger } from './ParcelArranger';
import { GenerateCustomSplitItems } from './GroupingHelper';
import { distributeIntoParcels, linesToContents } from '~/apps/smartsend/lib/parcels/allocate';
import { applyPerUnitWeight } from '~/apps/smartsend/lib/parcels/parcelWeight';
import { arrangementIsANoOp } from '~/apps/smartsend/lib/parcels/rules';
import { dragRowToParcel } from '~/apps/smartsend/lib/parcels/planEdits';
import { useParcelPlan } from '~/apps/smartsend/lib/parcels/useParcelPlan';
import { useTranslation } from 'react-i18next';
import { TranslationNamespaces } from '~/lib/constants';
import StickyButtons from '~/components/sticky';

export interface props {
  group: SmartSendItemGroup;
  create: (items: SmartSendItemApiModel[]) => void;
  countries?: Country[];
  productValidation?: InputRuleApiModel[];
  tariffSystem?: string;
  getPackages?: () => Promise<PackageApiModel[]>;
}

export const Custom: React.FC<props> = (props) => {
  const [initialDims] = useState(() => ({ ...props.group.Items[0].PackageDimensions }));
  const { t } = useTranslation(TranslationNamespaces.SMARTSEND);

  const rows = useMemo(
    () => props.group.Items.flatMap((item) => item.MarketplaceOrderLines.filter(FilterToSendableItems)),
    [props.group.Items]
  );

  const { plan, dimensions, setParcelCount, setDimensions, setPlan, edit } = useParcelPlan({
    initialParcelCount: 2,
    initialDimensions: initialDims,
    // Per-unit weight, matching Convert. Supplied for BOTH paths - here for a shipment-wide weight edit,
    // and around the seed below. Without this one, that edit would silently revert every box to an even
    // share of the weight.
    divideWeight: (plan, dims) => applyPerUnitWeight(plan, dims?.Weight),
    // Whole rows only - a row is never split here, because GenerateCustomSplitItems resolves each
    // row back to its originating order line and two rows sharing an Id would be ambiguous.
    seed: (count, dims) =>
      applyPerUnitWeight(
        distributeIntoParcels(linesToContents(rows), count, {
          granularity: 'rows',
          overflow: 'clamp',
          // The first shipment reuses the source item; the rest are created fresh.
          parcelId: (index) => (index === 0 ? props.group.Items[0].Id : null),
          // Seeded from the shipment dimensions, not left empty. Empty meant the per-box controls opened
          // blank while the shipment-wide section below already showed the order size - and they only
          // filled in if the customer happened to touch that control, since applyDimensions is what
          // pushes it down and the seed never did.
          dimensions: dims,
          canAdd: true,
          // Not the allocator's even split - weight is reapplied per unit below. Each box here becomes its
          // OWN shipment priced on its own weight, so an even split would declare a box of five items and a
          // box of one identically.
          splitWeight: false
        }),
        dims?.Weight
      )
  });

  /**
   * A row dragged from one shipment card to another in the preview.
   *
   * Deliberately dragRowToParcel rather than resolveRowDrag: these parcels each become a SEPARATE
   * shipment, so resolveRowDrag SEND_AS_SINGLE outcome - meaning a single-parcel shipment - has no
   * sense here. Dragging everything into one card means not splitting at all, which isANoOp below
   * already reports, so the plan is written through and the existing rule does the talking.
   *
   * Reaches no state the checkbox "Move to Shipment N" could not already reach: both go through
   * moveContentsToParcel, which prunes a card it empties.
   */
  const onDragRowToParcel = (contentUID: string, targetParcelUID: string) => {
    const next = dragRowToParcel(plan, contentUID, targetParcelUID);

    // Reference equality means the drop was refused.
    if (next !== plan) {
      setPlan(next);
    }
  };

  // Not a data-validity check: an arrangement with as many shipments as you started items with has
  // not rearranged anything, so there is nothing to create.
  /**
   * Which dimensions control is on screen. The two cannot both be - applyDimensions pushes one size
   * onto every shipment, so a shipment-wide edit would silently overwrite anything set per box.
   */
  const [previewing, setPreviewing] = useState(true);

  const isANoOp = arrangementIsANoOp(plan, props.group.Items.length);

  return (
    <SidePanelSections>
      <P2GText>
        {t('CUSTOM_SPLIT_INTRO_TEXT', {
          defaultValue:
            'This will allow you to split your items so they can be collected at different times or on different services.'
        })}
      </P2GText>
      <SidePanelSection
        title={t('SHIPMENT_BOXES_BEING_SHIPPED', { defaultValue: 'How many boxes are you going to use?' })}
      >
        <ParcelCountPicker
          availableParcels={parcelCountBound('clamp', rows.length)}
          selected={plan.Parcels.length}
          justify="flex-start"
          onSelect={setParcelCount}
          helperText={null}
        />
      </SidePanelSection>
      {isANoOp && (
        <P2GBox mb={3}>
          <P2GAlert type="warning">
            <P2GText variant="body2">
              {t('CUSTOM_SPLIT_INVALID_ARRANGEMENT_TEXT', {
                defaultValue:
                  'This arrangement matches the number of items you started with - rearrange into a different number of shipments to continue.'
              })}
            </P2GText>
          </P2GAlert>
        </P2GBox>
      )}
      <SidePanelSection title={t('SHIPMENT_SUMMARY_SUBTITLE', { defaultValue: 'Shipment Summary' })}>
        <ParcelArranger
          plan={plan}
          outcome="MANY_SHIPMENTS"
          // Rows now go through BuildSplitOrderLine on confirm, so per-row edits and manually added
          // rows survive. The fields were previously read-only only because those edits were being
          // discarded.
          edit={edit}
          arrange={edit}
          // Still off: splitting a row would change what gets created (one order line per unit
          // rather than one per row), which is a separate decision from fixing the data loss.
          content={{
            countries: props.countries,
            productValidation: props.productValidation,
            tariffSystem: props.tariffSystem,
            sender: props.group.Items[0]?.Sender,
            recipient: props.group.Items[0]?.Recipient,
            currencyCode: props.group.Items[0]?.CurrencyCode
          }}
          loadPreview={true}
          preview={previewing}
          onPreviewChange={setPreviewing}
          // Read only by the editor, so passing it unconditionally is safe; the swap lives below.
          perParcelDimensions={{ getPackages: props.getPackages }}
          onDragRowToParcel={onDragRowToParcel}
        />
      </SidePanelSection>
      {previewing && (
        <SidePanelSection title={t('AMEND_BOX_DIMENSION_SUBTITLE', { defaultValue: 'Amend Box Dimensions' })}>
          <P2GBox mb={1}>
            <P2GText variant="body2">
              <strong>
                {t('TOTAL_SHIPMENT_DIMENSION_NOTE', {
                  defaultValue:
                    'Note: Changes to this will feed into your consignment. Weight will be distributed and dimensions will be applied to each parcel.'
                })}
              </strong>
            </P2GText>
          </P2GBox>
          <DimensionsSelection
            dimensions={dimensions}
            onUpdate={setDimensions}
            getPackages={props.getPackages}
            startMinimised={false}
          />
        </SidePanelSection>
      )}
      <P2GAlert type="success">
        <P2GText variant="body2">
          {t('CUSTOM_SPLIT_SUMMARY_TEXT', {
            defaultValue: 'This will create {{shipmentCount}} separate shipments.',
            shipmentCount: plan.Parcels.length
          })}
        </P2GText>
      </P2GAlert>
      <StickyButtons>
        <P2GButton
          variant="primary"
          disabled={isANoOp}
          onClick={() => props.create(GenerateCustomSplitItems(props.group, plan, dimensions))}
        >
          {t('CONFIRM_CREATE_BUTTON', { defaultValue: 'Confirm and Create' })}
        </P2GButton>
      </StickyButtons>
    </SidePanelSections>
  );
};
